import { ComponentType, JSX, ReactNode, useEffect } from "react";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";
import { View, ViewProps } from "tamagui";

interface RotationWrapperProps extends ViewProps {
  children: ReactNode;
  reverse?: boolean;
}

export function RotationWrapper({
  children,
  reverse = false,
  ...rest
}: RotationWrapperProps) {
  const rotate = useSharedValue(0);

  useEffect(() => {
    rotate.value = withRepeat(
      withTiming(reverse ? -360 : 360, {
        duration: 1000,
        easing: Easing.linear,
      }),
      -1,
      false,
    );
  }, [rotate, reverse]);

  const rotation = useAnimatedStyle(
    () => ({
      transform: [{ rotate: `${rotate.value}deg` }],
    }),
    [rotate],
  );

  return (
    // Rotating icon's diagonals overflow container and change layout, so we crop the overflow
    <View overflow="hidden" {...rest}>
      <Animated.View style={rotation}>{children}</Animated.View>
    </View>
  );
}

export function withRotation<P extends JSX.IntrinsicAttributes>(
  Component: ComponentType<P>,
  options?: { reverse?: boolean },
) {
  const WrappedComponent = (props: P) => {
    return (
      <RotationWrapper reverse={options?.reverse}>
        <Component {...props} />
      </RotationWrapper>
    );
  };

  WrappedComponent.displayName = `withRotation(${Component.displayName || Component.name})`;

  return WrappedComponent;
}
